'use client'

import { useEffect, useState } from 'react'
import {
  getImpersonation,
  subscribeImpersonation,
  type ImpersonationSession,
} from '@/lib/impersonation'

/**
 * Reactive access to the current impersonation overlay (or null). Re-reads on start/stop and when
 * the tab regains focus (the overlay auto-expires on read, so a focus check drops a stale one).
 * Safe on the server — returns null there.
 */
export function useImpersonation(): ImpersonationSession | null {
  const [session, setSession] = useState<ImpersonationSession | null>(() => getImpersonation())

  useEffect(() => {
    const refresh = () => setSession(getImpersonation())
    refresh()
    const unsub = subscribeImpersonation(refresh)
    const onVisible = () => {
      if (document.visibilityState === 'visible') refresh()
    }
    document.addEventListener('visibilitychange', onVisible)
    return () => {
      unsub()
      document.removeEventListener('visibilitychange', onVisible)
    }
  }, [])

  return session
}
